/**
 * syncService — syncs EnergyMatch data to Supabase after each localStorage write.
 *
 * All sync functions are fire-and-forget: they return void and never throw.
 * Authenticated users get full sync (user_id + child_id).
 * Guests are localStorage-only — no Supabase sync (userId/childId will be empty).
 *
 * Sync status events are dispatched on `window` as CustomEvent('energymatch:sync')
 * so the UI can track progress without React prop-drilling.
 */

import { getSupabase } from '../lib/supabaseClient'
import type {
  ChildProfile,
  TrainingSession,
  DailyMealLog,
  ExtraSnackLog,
  HydrationLogEntry,
  DailyTrainingFeedback,
  SavedRecipe,
} from '../types'

export type SyncStatus = 'syncing' | 'synced' | 'error'

export interface SyncEvent {
  key: string
  status: SyncStatus
}

function dispatch(key: string, status: SyncStatus): void {
  window.dispatchEvent(
    new CustomEvent<SyncEvent>('energymatch:sync', { detail: { key, status } })
  )
}

// ---------------------------------------------------------------------------
// Sync functions — require authenticated userId + childId
// ---------------------------------------------------------------------------

/** Sync a child profile to the `children` table. */
export function syncChild(userId: string, child: ChildProfile): void {
  const supabase = getSupabase()
  if (!supabase || !userId) return
  const key = `child:${child.id}`
  dispatch(key, 'syncing')
  supabase
    .from('children')
    .upsert({
      id: child.id,
      user_id: userId,
      profile_data: child,
      updated_at: new Date().toISOString(),
    })
    .then(({ error }) => {
      if (error) console.error('[sync] children:', error.message)
      dispatch(key, error ? 'error' : 'synced')
    })
}

/** Sync all children for a user (used after migration or bulk update). */
export function syncAllChildren(userId: string, children: ChildProfile[]): void {
  children.forEach((c) => syncChild(userId, c))
}

export function syncSessions(
  userId: string,
  childId: string,
  dateKey: string,
  data: TrainingSession[]
): void {
  const supabase = getSupabase()
  if (!supabase || !userId || !childId) return
  const key = `sessions:${childId}:${dateKey}`
  dispatch(key, 'syncing')
  supabase
    .from('training_sessions')
    .upsert({
      user_id: userId,
      child_id: childId,
      date_key: dateKey,
      data,
      updated_at: new Date().toISOString(),
    })
    .then(({ error }) => {
      if (error) console.error('[sync] training_sessions:', error.message)
      dispatch(key, error ? 'error' : 'synced')
    })
}

export function syncMealLog(
  userId: string,
  childId: string,
  dateKey: string,
  slots: DailyMealLog,
  extraSnacks: ExtraSnackLog[],
  hydration: HydrationLogEntry[]
): void {
  const supabase = getSupabase()
  if (!supabase || !userId || !childId) return
  const key = `meals:${childId}:${dateKey}`
  dispatch(key, 'syncing')
  supabase
    .from('meal_logs')
    .upsert({
      user_id: userId,
      child_id: childId,
      date_key: dateKey,
      slots,
      extra_snacks: extraSnacks,
      hydration,
      updated_at: new Date().toISOString(),
    })
    .then(({ error }) => {
      if (error) console.error('[sync] meal_logs:', error.message)
      dispatch(key, error ? 'error' : 'synced')
    })
}

export function syncFeedback(
  userId: string,
  childId: string,
  dateKey: string,
  data: DailyTrainingFeedback
): void {
  const supabase = getSupabase()
  if (!supabase || !userId || !childId) return
  const key = `feedback:${childId}:${dateKey}`
  dispatch(key, 'syncing')
  supabase
    .from('training_feedback')
    .upsert({
      user_id: userId,
      child_id: childId,
      date_key: dateKey,
      data,
      updated_at: new Date().toISOString(),
    })
    .then(({ error }) => {
      if (error) console.error('[sync] training_feedback:', error.message)
      dispatch(key, error ? 'error' : 'synced')
    })
}

/** Sync a saved recipe to the `saved_recipes` table. */
export function syncRecipe(userId: string, childId: string, recipe: SavedRecipe): void {
  const supabase = getSupabase()
  if (!supabase || !userId || !childId) return
  const key = `recipe:${childId}:${recipe.id}`
  dispatch(key, 'syncing')
  supabase
    .from('saved_recipes')
    .upsert({
      id: recipe.id,
      user_id: userId,
      child_id: childId,
      name: recipe.name,
      servings: recipe.servings ?? null,
      photo_thumbnail_url: recipe.photoThumbnailUrl ?? null,
      food_entries: recipe.foodEntries,
      extracted_ingredients: recipe.extractedIngredients ?? null,
      meal_tags: recipe.mealTags ?? null,
      updated_at: new Date().toISOString(),
    })
    .then(({ error }) => {
      if (error) console.error('[sync] saved_recipes:', error.message)
      dispatch(key, error ? 'error' : 'synced')
    })
}

/** Delete a saved recipe from the `saved_recipes` table. */
export function deleteRecipeSync(userId: string, recipeId: string): void {
  const supabase = getSupabase()
  if (!supabase || !userId) return
  const key = `recipe:delete:${recipeId}`
  dispatch(key, 'syncing')
  supabase
    .from('saved_recipes')
    .delete()
    .eq('id', recipeId)
    .eq('user_id', userId)
    .then(({ error }) => {
      if (error) console.error('[sync] saved_recipes delete:', error.message)
      dispatch(key, error ? 'error' : 'synced')
    })
}

// ---------------------------------------------------------------------------
// Load from cloud — called on login when localStorage is empty (new browser/device)
// ---------------------------------------------------------------------------

export interface LoadResult {
  /** True if Supabase had children for this user. */
  found: boolean
  /** True if a network/server error prevented loading (distinct from "no data"). */
  error: boolean
}

/**
 * Fetch all user data from Supabase and write it into localStorage.
 * Returns { found, error } — callers can distinguish "no account data" from "load failed".
 * Never throws — errors are logged and silently ignored.
 */
export async function loadAllUserData(userId: string): Promise<LoadResult> {
  const supabase = getSupabase()
  if (!supabase || !userId) return { found: false, error: false }

  try {
    const { data: childRows, error: childError } = await supabase
      .from('children')
      .select('profile_data')
      .eq('user_id', userId)

    if (childError) {
      console.error('[sync] loadAllUserData: children fetch failed:', childError.message)
      return { found: false, error: true }
    }
    if (!childRows || childRows.length === 0) return { found: false, error: false }

    const children = (childRows as { profile_data: ChildProfile }[]).map((r) => r.profile_data)
    try {
      localStorage.setItem('energymatch-children', JSON.stringify(children))
    } catch { /* quota */ }

    await Promise.all(
      children.map(async (child) => {
        const childId = child.id

        const [{ data: sessionRows }, { data: mealRows }, { data: feedbackRows }, { data: recipeRows }] =
          await Promise.all([
            supabase
              .from('training_sessions')
              .select('date_key, data')
              .eq('user_id', userId)
              .eq('child_id', childId),
            supabase
              .from('meal_logs')
              .select('date_key, slots, extra_snacks, hydration')
              .eq('user_id', userId)
              .eq('child_id', childId),
            supabase
              .from('training_feedback')
              .select('date_key, data')
              .eq('user_id', userId)
              .eq('child_id', childId),
            supabase
              .from('saved_recipes')
              .select('id, name, servings, photo_thumbnail_url, food_entries, extracted_ingredients, meal_tags, created_at')
              .eq('user_id', userId)
              .eq('child_id', childId),
          ])

        try {
          if (sessionRows && sessionRows.length > 0) {
            const byDate: Record<string, TrainingSession[]> = {}
            ;(sessionRows as { date_key: string; data: TrainingSession[] }[]).forEach(
              (r) => { byDate[r.date_key] = r.data }
            )
            localStorage.setItem(`energymatch-sessions-v2-${childId}`, JSON.stringify(byDate))
          }
        } catch { /* quota */ }

        try {
          if (mealRows && mealRows.length > 0) {
            const slots: Record<string, DailyMealLog> = {}
            const extraSnacks: Record<string, ExtraSnackLog[]> = {}
            const hydration: Record<string, HydrationLogEntry[]> = {}
            ;(mealRows as {
              date_key: string
              slots: DailyMealLog
              extra_snacks: ExtraSnackLog[]
              hydration: HydrationLogEntry[]
            }[]).forEach((r) => {
              slots[r.date_key] = r.slots
              extraSnacks[r.date_key] = r.extra_snacks
              hydration[r.date_key] = r.hydration
            })
            localStorage.setItem(`energymatch-daily-logs-${childId}`, JSON.stringify(slots))
            localStorage.setItem(`energymatch-extra-snacks-${childId}`, JSON.stringify(extraSnacks))
            localStorage.setItem(`energymatch-hydration-${childId}`, JSON.stringify(hydration))
          }
        } catch { /* quota */ }

        try {
          if (feedbackRows && feedbackRows.length > 0) {
            const byDate: Record<string, DailyTrainingFeedback> = {}
            ;(feedbackRows as { date_key: string; data: DailyTrainingFeedback }[]).forEach(
              (r) => { byDate[r.date_key] = r.data }
            )
            localStorage.setItem(`energymatch-training-feedback-${childId}`, JSON.stringify(byDate))
          }
        } catch { /* quota */ }

        try {
          if (recipeRows && recipeRows.length > 0) {
            // Preserve any mealTags that exist locally but aren't yet in the cloud column
            const localRaw = localStorage.getItem(`energymatch-saved-recipes-${childId}`)
            const localTagsMap: Record<string, SavedRecipe['mealTags']> = {}
            if (localRaw) {
              try {
                const local = JSON.parse(localRaw) as SavedRecipe[]
                for (const r of local) {
                  if (r.id && r.mealTags) localTagsMap[r.id] = r.mealTags
                }
              } catch { /* ignore */ }
            }
            const recipes: SavedRecipe[] = (recipeRows as {
              id: string
              name: string
              servings: number | null
              photo_thumbnail_url: string | null
              food_entries: SavedRecipe['foodEntries']
              extracted_ingredients: SavedRecipe['extractedIngredients']
              meal_tags: SavedRecipe['mealTags']
              created_at: string
            }[]).map((r) => ({
              id: r.id,
              name: r.name,
              foodEntries: r.food_entries ?? [],
              createdAt: r.created_at,
              servings: r.servings ?? undefined,
              photoThumbnailUrl: r.photo_thumbnail_url ?? undefined,
              extractedIngredients: r.extracted_ingredients ?? undefined,
              mealTags: r.meal_tags ?? localTagsMap[r.id],
            }))
            localStorage.setItem(`energymatch-saved-recipes-${childId}`, JSON.stringify(recipes))
          }
        } catch { /* quota */ }
      })
    )
  } catch (err) {
    console.error('[sync] loadAllUserData failed:', err)
    return { found: false, error: true }
  }
  return { found: true, error: false }
}
